import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Flex } from 'components'

import { ERROR_CODES } from 'api'
import withNavigationBar from 'hoc/nav-bar/with-navigation-bar'
import useLeadDetail from 'hooks/use-lead-detail'
import useUser from 'hooks/use-user'
import TransitionPage from 'modules/TransitionPage'
import AccessDenied from 'modules/shared/AccessDenied'
import ShowError from 'modules/shared/ShowError'
import useOpenExternalLeadUrl from 'modules/Leads/hooks/use-open-external-lead-url'
import { Lead, useAppDispatch } from 'store'
import { colors } from 'utils/colors'
import { ScopeModalProvider } from 'hooks/use-scope-modal'
import { LeadFormConfigProvider } from 'modules/Leads/context'
import { ROUTES } from '../../../constants/constants'

const LeftSection = React.lazy(() => import('./LeftSection'))
const RightSection = React.lazy(() => import('./RightSection'))
const LeadActivities = React.lazy(() => import('./Windows'))

export const leadDetailRootId = 'lead-detail-root'

const LeadDetail = () => {
  const navigate = useNavigate()
  const { leadId = '' } = useParams<{ leadId?: string; leadAppointmentId?: string }>()
  const dispatch = useAppDispatch()

  const { currentBranch, currentUser } = useUser()
  const { data, isFetching, isLoading, isError, error } = useLeadDetail({
    queryOptions: {
      enabled: true,
    },
  })

  useOpenExternalLeadUrl()

  useEffect(
    () => () => {
      dispatch(Lead.clearLeadDetailWindows())
    },
    [dispatch, leadId],
  )

  useEffect(() => {
    if (!currentBranch?.id) {
      navigate(`/${ROUTES.LEADS}`)
    }
  }, [navigate, currentBranch])

  if (!currentUser.id) return <div>Invalid user details</div>

  if (isLoading) {
    return <TransitionPage message="lead_details_loading" />
  }

  if (isError) {
    if (error && error.code === ERROR_CODES.INVALID_PERMISSION) return <AccessDenied />

    return <ShowError error={error} />
  }

  return (
    <ScopeModalProvider>
      <LeadFormConfigProvider initialConditionData={data?.leadTypeId ? { lead_type_id: data.leadTypeId } : undefined}>
        <Container id={leadDetailRootId}>
          <Flex width="100%" gap="0" maxWidth={1500} backgroundColor={colors.white}>
            <LeftContainer>
              <LeftSection />
            </LeftContainer>
            <RightContainer isFetching={isFetching} fetchingStyle="spinner">
              <RightSection />
            </RightContainer>
          </Flex>
          <LeadActivities />
        </Container>
      </LeadFormConfigProvider>
    </ScopeModalProvider>
  )
}

export default withNavigationBar(LeadDetail)

const Container = styled.section`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: ${(p) => p.theme.colors.gray1};

  & ::-webkit-scrollbar {
    width: 1rem;
  }

  & ::-webkit-scrollbar-track {
    background-color: transparent;
  }

  & ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.gray3};
    border-radius: 1rem;
    border: 4px solid transparent;
    background-clip: content-box;
  }

  & ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.gray5};
  }
`

const LeftContainer = styled(Box)`
  width: 500px;
  height: 100%;
`

const RightContainer = styled(Box)`
  width: calc(100% - 500px);
  min-width: 700px;
  height: 100%;
  border-left: 1px solid ${(p) => p.theme.colors.gray3};
  overflow: hidden;
`
